/**
 * MailWeergave (sessie 186, punt 7): één weergave van een mail, in de zaak
 * en in het logboek op de klantkaart (tab Contact).
 *
 * - Is er opmaak (`mail.html`), dan de geschoonde HTML uit
 *   `app/services/mail_weergave.py` in het veilige vlak (`Mailvlak`:
 *   sandbox zonder scripts, strakke CSP).
 * - Anders de platte tekst, al zonder markeringen van de server (geen
 *   sterretjes om vet, geen markdown-tekens).
 * - Het eerdere bericht (citaat, onze orderbevestiging, doorgestuurd deel)
 *   staat ingeklapt onder "Eerdere berichten", standaard dicht. Alleen als
 *   er geen nieuw deel is (een kale doorstuur) staat het meteen open.
 * - Afbeeldingen van buiten laden pas na een klik op "Afbeeldingen laden";
 *   die klik geldt voor deze mail, ook voor het eerdere bericht.
 *
 * Selectors voor toetsen: `[data-testid=mailweergave]`, `mail-tekst`,
 * `eerdere-berichten` (de knop, met aria-expanded), `eerdere-inhoud`,
 * `afbeeldingen-laden`; de iframes hebben de titels "Mail van de klant" en
 * "Eerdere berichten".
 */
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { v6Api, type V6Mail } from "../api";
import { Icoon } from "../Icoon";
import { metLinks } from "./links";
import { Mailvlak } from "./Mailvlak";

import "./mailvlak.css";

export interface MailWeergaveProps {
  emailId: string;
  mail: V6Mail;
  /** Titel van het iframe met de mail zelf. */
  titel?: string;
}

/** Platte tekst als één blok; regels en witregels blijven staan (pre-wrap). */
function Tekst({ tekst, testId }: { tekst: string; testId: string }) {
  return (
    <div className="vo-mailtekst" data-testid={testId}>
      {metLinks(tekst)}
    </div>
  );
}

export function MailWeergave({ emailId, mail, titel = "Mail van de klant" }: MailWeergaveProps) {
  const metOpmaak = Boolean(mail.html || mail.citaat_html);
  const nieuw = metOpmaak ? mail.html : (mail.tekst ?? "").trim();
  const citaatHtml = mail.citaat_html ?? "";
  const citaatTekst = (mail.citaat ?? "").trim();
  const heeftCitaat = metOpmaak ? Boolean(citaatHtml) : Boolean(citaatTekst);

  // Per mail: een andere mail in hetzelfde onderdeel begint dicht en zonder
  // externe afbeeldingen.
  const [stand, setStand] = useState({ emailId, open: !nieuw, extern: false });
  if (stand.emailId !== emailId) setStand({ emailId, open: !nieuw, extern: false });
  const open = stand.emailId === emailId ? stand.open : !nieuw;
  const toonExtern = mail.extern && stand.emailId === emailId && stand.extern;

  return (
    <div className="vo-mailweergave" data-testid="mailweergave">
      {metOpmaak && mail.extern && !toonExtern && (
        <button
          type="button"
          className="v6-knop vo-mailvlak-knop"
          data-testid="afbeeldingen-laden"
          onClick={() => setStand({ emailId, open, extern: true })}
        >
          Afbeeldingen laden
        </button>
      )}
      {metOpmaak
        ? nieuw && <Mailvlak emailId={emailId} html={nieuw} toonExtern={toonExtern} titel={titel} />
        : nieuw && <Tekst tekst={nieuw} testId="mail-tekst" />}
      {heeftCitaat && (
        <>
          <button
            type="button"
            className="vo-eerdere-knop"
            data-testid="eerdere-berichten"
            aria-expanded={open}
            onClick={() => setStand({ emailId, open: !open, extern: stand.extern })}
          >
            <Icoon naam="pijl" klein />
            Eerdere berichten
          </button>
          {open && (
            <div className="vo-eerdere" data-testid="eerdere-inhoud">
              {metOpmaak ? (
                <Mailvlak
                  emailId={emailId}
                  html={citaatHtml}
                  toonExtern={toonExtern}
                  titel="Eerdere berichten"
                />
              ) : (
                <Tekst tekst={citaatTekst} testId="eerdere-tekst" />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * De mail achter een logboekregel: pas opgehaald als iemand hem openklapt,
 * daarna dezelfde weergave als in de zaak.
 */
export function MailUitLogboek({ emailId }: { emailId: string }) {
  const q = useQuery({
    queryKey: ["v6", "mailweergave", emailId],
    queryFn: () => v6Api.mailWeergave(emailId),
    staleTime: 5 * 60_000,
  });
  if (q.isLoading) return <p className="stil">Mail wordt geladen.</p>;
  if (q.isError || !q.data)
    return (
      <p className="v6-fout" role="alert">
        De mail kon niet geladen worden.
      </p>
    );
  return <MailWeergave emailId={emailId} mail={q.data} titel="Mail" />;
}

const MD_LINK = /\[([^\]\n]+)\]\((https?:\/\/[^)\s]+|mailto:[^)\s]+)\)/g;
const MD_VET = /\*\*(?=\S)(.+?)(?<=\S)\*\*|__(?=\S)(.+?)(?<=\S)__/g;
const MD_STER = /(?<![\p{L}\p{N}_*])\*(?=[^\s*])([^*\n]+?)(?<=[^\s*])\*(?![\p{L}\p{N}_*])/gu;
const MD_KOP = /^[ \t]*#{1,6}[ \t]+/gm;
const MD_OPSOMMING = /^([ \t]*)[*+][ \t]+/gm;
const MD_CODE = /`([^`\n]+)`/g;

/**
 * Tekst zonder opmaaktekens, zelfde regels als `zonder_markering` in
 * `app/services/mail_weergave.py`: voor teksten die niet via die functie
 * lopen, zoals de samenvatting van een mail in het logboek.
 */
export function zonderMarkering(tekst: string | null | undefined): string {
  if (!tekst) return "";
  return tekst
    .replace(MD_LINK, (_, t: string, url: string) => {
      const kaal = url.replace(/^mailto:/, "");
      const schoon = t.trim();
      return schoon === url || schoon === kaal ? url : `${schoon} (${kaal})`;
    })
    .replace(MD_VET, (_, a?: string, b?: string) => a ?? b ?? "")
    .replace(MD_OPSOMMING, "$1- ")
    .replace(MD_STER, "$1")
    .replace(MD_KOP, "")
    .replace(MD_CODE, "$1");
}

export default MailWeergave;
